import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, IconButton, TextField } from '@mui/material';
import { ArrowBack, Add, Remove, Delete } from '@mui/icons-material';

const ShoppingCart = () => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  console.log('🛒 ShoppingCart component rendered');

  // Get URL parameters
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      mode: params.get('mode'),
      storeId: params.get('storeId'),
      sheetId: params.get('sheetId')
    };
  };

  const { mode, storeId, sheetId } = getUrlParams();

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('customerToken');
      const customerData = localStorage.getItem('customerData');
      if (token && customerData) {
        try {
          const parsedCustomer = JSON.parse(customerData);
          console.log('🛒 ShoppingCart: Found authenticated customer:', parsedCustomer.name);
          setUser(parsedCustomer);
        } catch (error) {
          console.error('🛒 ShoppingCart: Error parsing customer data:', error);
        }
      } else {
        console.log('🛒 ShoppingCart: No customer authentication found');
        setUser(null);
      }
    };

    checkAuth();

    // Also listen for storage changes (login/logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'customerToken' || e.key === 'customerData') {
        console.log('🛒 ShoppingCart: Auth storage changed, re-checking auth');
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  console.log('🛒 URL params:', { mode, storeId, sheetId });

  // Load cart on component mount and when user state changes
  useEffect(() => {
    console.log('🛒 Loading cart...');
    loadCart();
  }, [user, storeId]);

  // Save cart to database when cart state changes (for authenticated users)
  useEffect(() => {
    if (user && user.id && cart.length >= 0) {
      saveCartToDatabase(cart);
    }
  }, [cart, user]);

  const loadCart = async () => {
    try {
      console.log('🛒 loadCart called with user:', user ? `${user.name} (${user.id})` : 'null');
      console.log('🛒 URL params - storeId:', storeId, 'sheetId:', sheetId);

      if (user && user.id) {
        // Authenticated customer - load from database
        console.log('🛒 Loading cart from database for customer:', user.id);

        try {
          const token = localStorage.getItem('customerToken');
          if (!token) {
            console.log('🛒 No auth token found, cannot load from database');
            setCart([]);
            setLoading(false);
            return;
          }

          const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/cart`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              console.log('🛒 Successfully loaded cart from database:', result.cart.length, 'items');
              setCart(Array.isArray(result.cart) ? result.cart : []);
            } else {
              console.error('🛒 Failed to load cart from database:', result.message);
              setCart([]);
            }
          } else {
            console.error('🛒 Database cart load failed with status:', response.status);
            setCart([]);
          }
        } catch (dbError) {
          console.error('🛒 Error loading cart from database:', dbError);
          // Fallback to localStorage
          loadCartFromLocalStorage();
        }
      } else {
        // Unauthenticated user - load from localStorage
        console.log('🛒 Loading cart from localStorage for unauthenticated user');
        loadCartFromLocalStorage();
      }
    } catch (error) {
      console.error('🛒 Error loading cart:', error);
      setCart([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCartFromLocalStorage = () => {
    try {
      // Use customer-specific cart key for authenticated customers
      // This matches ProductCatalog.js logic for cart persistence
      let cartKey;
      if (user && user.id) {
        // Authenticated customer - use customer ID for cart persistence
        cartKey = `cart_customer_${user.id}`;
        console.log('🛒 Customer cart detected, using key:', cartKey);
      } else if (storeId) {
        // Single store mode for unauthenticated users
        cartKey = `cart_store_${storeId}`;
        console.log('🛒 Store mode detected, using key:', cartKey);
      } else {
        // Full marketplace mode for unauthenticated users
        cartKey = 'cart_marketplace';
        console.log('🛒 Marketplace cart, using key:', cartKey);
      }

      console.log('🛒 Final cart key:', cartKey);

      // Load from localStorage with proper error handling
      const savedCart = localStorage.getItem(cartKey);
      console.log('🛒 Raw saved cart data:', savedCart);

      if (savedCart && savedCart !== 'undefined' && savedCart !== 'null' && savedCart.trim() !== '') {
        try {
          const parsedCart = JSON.parse(savedCart);
          console.log('🛒 Successfully parsed cart with', Array.isArray(parsedCart) ? parsedCart.length : 'invalid', 'items');
          setCart(Array.isArray(parsedCart) ? parsedCart : []);
        } catch (parseError) {
          console.error('🛒 Error parsing cart data:', parseError, 'Raw data:', savedCart);
          setCart([]);
        }
      } else {
        console.log('🛒 No valid saved cart found for key:', cartKey);
        setCart([]);
      }
    } catch (error) {
      console.error('🛒 Error loading cart from localStorage:', error);
      setCart([]);
    }
  };

  const saveCartToDatabase = async (cartData) => {
    if (!user || !user.id) return;

    try {
      const token = localStorage.getItem('customerToken');
      if (!token) return;

      console.log('🛒 Saving cart to database:', cartData.length, 'items');

      // The cart is automatically saved via API calls when items are added/updated
      // This function is mainly for syncing any local changes
      // Individual operations (add/update/remove) already save to database

    } catch (error) {
      console.error('🛒 Error saving cart to database:', error);
    }
  };

  const clearCart = async () => {
    console.log('🛒 Clearing cart');

    if (user && user.id) {
      // Authenticated customer - clear from database
      try {
        const token = localStorage.getItem('customerToken');
        if (token) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/cart`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            console.log('🛒 Cart cleared from database');
          } else {
            console.error('🛒 Failed to clear cart from database');
          }
        }
      } catch (error) {
        console.error('🛒 Error clearing cart from database:', error);
      }
    } else {
      // Unauthenticated user - clear from localStorage
      let cartKey;
      if (storeId) {
        // Single store mode for unauthenticated users
        cartKey = `cart_store_${storeId}`;
      } else {
        // Full marketplace mode for unauthenticated users
        cartKey = 'cart_marketplace';
      }

      console.log('🛒 Clearing cart with key:', cartKey);
      localStorage.removeItem(cartKey);
    }

    setCart([]);
  };

  const updateCartQuantity = async (productId, newQuantity) => {
    console.log('🛒 Updating quantity:', productId, newQuantity);

    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const updatedCart = cart.map(item =>
      item.productId === productId
        ? { ...item, quantity: newQuantity, total: newQuantity * (item.unitPrice || 0) }
        : item
    );

    setCart(updatedCart);

    // Save to database if authenticated
    if (user && user.id) {
      try {
        const token = localStorage.getItem('customerToken');
        if (token) {
          await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/cart/update`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              productId,
              quantity: newQuantity
            }),
          });
          console.log('🛒 Quantity updated in database');
        }
      } catch (error) {
        console.error('🛒 Error updating quantity in database:', error);
      }
    } else {
      // Save to localStorage for unauthenticated users
      saveCart(updatedCart);
    }
  };

  const removeFromCart = async (productId) => {
    console.log('🛒 Removing item:', productId);

    const updatedCart = cart.filter(item => item.productId !== productId);
    setCart(updatedCart);

    // Save to database if authenticated
    if (user && user.id) {
      try {
        const token = localStorage.getItem('customerToken');
        if (token) {
          await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/cart/remove/${productId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          console.log('🛒 Item removed from database');
        }
      } catch (error) {
        console.error('🛒 Error removing item from database:', error);
      }
    } else {
      // Save to localStorage for unauthenticated users
      saveCart(updatedCart);
    }
  };

  const saveCart = (cartData) => {
    // Use customer-specific cart key for authenticated customers
    // This matches ProductCatalog.js logic for cart persistence
    let cartKey;
    if (user && user.id) {
      // Authenticated customer - use customer ID for cart persistence
      cartKey = `cart_customer_${user.id}`;
    } else if (storeId) {
      // Single store mode for unauthenticated users
      cartKey = `cart_store_${storeId}`;
    } else {
      // Full marketplace mode for unauthenticated users
      cartKey = 'cart_marketplace';
    }

    console.log('🛒 Saving cart to key:', cartKey, 'with items:', cartData?.length || 0);
    localStorage.setItem(cartKey, JSON.stringify(cartData));
  };

  const getCartTotal = () => {
    return (cart || []).reduce((total, item) => total + (item.total || 0), 0);
  };

  const getItemCount = () => {
    return (cart || []).reduce((total, item) => total + (item.quantity || 0), 0);
  };

  console.log('🛒 Current cart state:', cart);
  console.log('🛒 Cart total:', getCartTotal());
  console.log('🛒 Item count:', getItemCount());

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading Cart...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => window.location.href = '/products'}
          >
            Continue Shopping
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Shopping Cart
          </Typography>
        </Box>

        {(cart || []).length > 0 && (
          <Button variant="outlined" color="error" onClick={clearCart}>
            Clear Cart
          </Button>
        )}
      </Box>

      {/* Cart Content */}
      {(cart || []).length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add some products to get started
            </Typography>
            <Button variant="contained" onClick={() => window.location.href = '/products'}>
              Browse Products
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cart Items ({getItemCount()})
            </Typography>

            {/* Cart Items List */}
            {(cart || []).map((item, index) => (
              <Box key={item.productId || index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {item.product?.name || 'Unknown Product'}
                    </Typography>
                    {item.product?.businessName && (
                      <Typography variant="caption" color="primary">
                        🏪 {item.product.businessName}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeFromCart(item.productId)}
                  >
                    <Delete />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      ₹{item.unitPrice || 0} each
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => updateCartQuantity(item.productId, (item.quantity || 1) - 1)}
                    >
                      <Remove />
                    </IconButton>

                    <TextField
                      size="small"
                      value={item.quantity || 1}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        updateCartQuantity(item.productId, value);
                      }}
                      inputProps={{ min: 1, style: { textAlign: 'center', width: '40px' } }}
                      sx={{ width: '60px' }}
                    />

                    <IconButton
                      size="small"
                      onClick={() => updateCartQuantity(item.productId, (item.quantity || 1) + 1)}
                    >
                      <Add />
                    </IconButton>
                  </Box>

                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '80px', textAlign: 'right' }}>
                    ₹{item.total || 0}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* Order Summary */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom>Order Summary</Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal ({getItemCount()} items)</Typography>
                <Typography>₹{getCartTotal().toFixed(2)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Shipping</Typography>
                <Typography>{getCartTotal() >= 500 ? 'FREE' : '₹50.00'}</Typography>
              </Box>

              <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6" color="primary">
                    ₹{(getCartTotal() + (getCartTotal() >= 500 ? 0 : 50)).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {getCartTotal() < 500 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Add ₹{(500 - getCartTotal()).toFixed(2)} more for free shipping!
                </Typography>
              )}
            </Box>

            {/* Checkout Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              onClick={() => {
                // Build checkout URL with correct parameters based on current cart mode
                let checkoutUrl = '/checkout';
                if (storeId) {
                  // Single store checkout
                  checkoutUrl = `/checkout?storeId=${storeId}`;
                } else if (user && user.id) {
                  // Authenticated customer checkout - cart is already user-specific
                  checkoutUrl = '/checkout?mode=customer';
                } else {
                  // Unauthenticated marketplace checkout
                  checkoutUrl = '/checkout?mode=marketplace';
                }
                console.log('🛒 Navigating to checkout:', checkoutUrl);
                window.location.href = checkoutUrl;
              }}
            >
              Proceed to Checkout
            </Button>
          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default ShoppingCart;